import ahoy from 'ahoy.js'
import { router } from '@inertiajs/react'

// Clicks are tracked on real controls only. A bare `input` selector would be worse
// than useless: ahoy.js records `this.value` for inputs, so tracking every input
// would copy whatever somebody typed — an email address, a password — into an
// analytics table. Submit and button inputs are safe because their value is the
// label printed on them.
const CLICK_SELECTOR = 'a, button, input[type=submit], input[type=button], [role="button"]'

// Forces ahoy.js off `navigator.sendBeacon` and onto XMLHttpRequest.
//
// This is load-bearing rather than cosmetic. A beacon cannot set request headers, so
// it cannot send the CSRF token, and Ahoy's endpoints inherit `protect_from_forgery
// with: :null_session` — which nulls the cookie jar on an unverified request. Every
// beacon-sent event would therefore be stored with no user and no organization
// attached. ahoy.js only takes the beacon path when `headers` is empty
// (`canUseBeacon`), so a single header is what keeps identity on the row.
//
// The cost is that events queued as the tab closes can be lost, which is the right
// trade: an anonymous event is worse than a missing one.
const FORCE_XHR_HEADERS = { 'X-Requested-With': 'XMLHttpRequest' }

let started = false

export function startTracking() {
  // Inertia keeps one document alive for the whole session, so this module can be
  // evaluated more than once during development hot reloads. Click tracking attaches
  // a delegated document listener, and attaching it twice doubles every event.
  if (started) return
  started = true

  ahoy.configure({
    visitsUrl: '/ahoy/visits',
    eventsUrl: '/ahoy/events',
    headers: FORCE_XHR_HEADERS,
    useBeacon: false,
    // Cookies are same-site and the endpoints are same-origin, so the browser sends
    // the session cookie without this; setting it would only matter behind a
    // different origin, which the Inertia contract does not allow.
    withCredentials: false,
    startOnReady: true,
    trackVisits: true,
  })

  // Delegated on document, so it survives every Inertia navigation and does not need
  // re-arming below.
  ahoy.trackClicks(CLICK_SELECTOR)

  // The first view has to be fired by hand: `router.on('navigate')` reports
  // subsequent visits, not the page the browser landed on.
  trackView()
  router.on('navigate', () => {
    // Deferred a frame so document.title is the new page's title rather than the one
    // being left — Inertia swaps the component before the head is updated, and
    // reading it too early files every view under the previous page's name.
    requestAnimationFrame(trackView)
  })
}

function trackView() {
  ahoy.trackView()
}
